import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { downloadCsv } from "./reportUtils";
import { Empty, ErrorState } from "./ReportKit";

const normalize = (res, size, page) => {
  if (Array.isArray(res))
    return { rows: res, total: null, hasMore: res.length >= size };
  const rows = res?.content || [];
  const pages = res?.totalPages ?? 0;
  return { rows, total: res?.totalElements ?? null, hasMore: pages > page + 1 };
};

/**
 * Server-paginated table. `fetcher(params)` receives { ...params, page, size, sort?, search? }
 * and may resolve either a Spring page ({content,totalElements,...}) or a plain array.
 * columns: [{ key, header, render?, sortable?, sortKey?, exportValue?, align? }]
 */
const ServerTable = ({
  id,
  bare,
  hideTitle,
  title,
  columns,
  fetcher,
  params,
  pageSize = 25,
  searchable = true,
  sortable = true,
  defaultSort,
  toolbar,
  exportName = "report",
  onData,
  emptyText = "No records match the selected period and filters.",
}) => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(pageSize);
  const [sort, setSort] = useState(defaultSort || "");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [state, setState] = useState({
    loading: true,
    error: null,
    rows: [],
    total: null,
    hasMore: false,
  });
  const sequence = useRef(0);
  const onDataRef = useRef(onData);
  onDataRef.current = onData;
  const paramsKey = JSON.stringify(params || {});

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [paramsKey, q, sort, size]);

  const load = useCallback(async () => {
    const id = ++sequence.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await fetcher({
        ...params,
        page,
        size,
        ...(sortable && sort ? { sort } : {}),
        ...(searchable && q ? { search: q } : {}),
      });
      if (id !== sequence.current) return;
      const next = normalize(res, size, page);
      setState({ loading: false, error: null, ...next });
      if (onDataRef.current) onDataRef.current(next.rows);
    } catch (e) {
      if (id !== sequence.current) return;
      setState({
        loading: false,
        error: e?.response?.data?.message || "Unable to load records.",
        rows: [],
        total: null,
        hasMore: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, page, size, sort, q]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSort = (col) => {
    const field = col.sortKey || col.key;
    setSort((s) => (s === `${field},desc` ? `${field},asc` : `${field},desc`));
  };
  const sortState = (col) => {
    const field = col.sortKey || col.key;
    if (sort === `${field},asc`) return "mdi:arrow-up";
    if (sort === `${field},desc`) return "mdi:arrow-down";
    return "mdi:unfold-more-horizontal";
  };

  const { rows, total, hasMore, loading, error } = state;
  const from = rows.length ? page * size + 1 : 0;
  const to = page * size + rows.length;

  return (
    <section
      id={id}
      className={`rp-panel rp-table-panel ${bare ? "rp-table-bare" : ""}`}
    >
      <header className="rp-panel-head">
        <div>
          {!hideTitle && <h3>{title}</h3>}
          {total !== null && <p>{total.toLocaleString()} records</p>}
        </div>
        <div className="rp-table-tools">
          {toolbar}
          {searchable && (
            <label className="rp-search">
              <Icon icon="mdi:magnify" />
              <input
                aria-label={`Search ${title}`}
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          )}
          <button
            type="button"
            className="rp-btn"
            onClick={() => downloadCsv(exportName, columns, rows)}
            disabled={!rows.length}
          >
            <Icon icon="mdi:download-outline" /> Export page
          </button>
        </div>
      </header>
      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <div className={`rp-table-wrap ${loading ? "rp-loading" : ""}`}>
          <table className="rp-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} style={{ textAlign: c.align || "left" }}>
                    {sortable && c.sortable ? (
                      <button type="button" onClick={() => toggleSort(c)}>
                        {c.header}
                        <Icon icon={sortState(c)} />
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={
                    row.id ??
                    row.clientId ??
                    row.leadId ??
                    row.enquiryId ??
                    row.historyId ??
                    row.operationId ??
                    i
                  }
                >
                  {columns.map((c) => (
                    <td key={c.key} style={{ textAlign: c.align || "left" }}>
                      {c.render
                        ? c.render(row[c.key], row)
                        : (row[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && !loading && <Empty text={emptyText} />}
        </div>
      )}
      <footer className="rp-table-foot">
        <span>
          {total !== null
            ? `${from}–${to} of ${total.toLocaleString()}`
            : `Page ${page + 1}`}
        </span>
        <div>
          <select
            aria-label="Rows per page"
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
          <button
            type="button"
            className="rp-btn"
            disabled={page === 0 || loading}
            onClick={() => setPage((p) => p - 1)}
          >
            <Icon icon="mdi:chevron-left" /> Previous
          </button>
          <button
            type="button"
            className="rp-btn"
            disabled={!hasMore || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next <Icon icon="mdi:chevron-right" />
          </button>
        </div>
      </footer>
    </section>
  );
};

export default ServerTable;
